import React from 'react';
import ProfileImage from '../../profileImage';

const TeamCard = ({ 
  team, 
  teamPlayers, 
  isOwnTeam, 
  isSentRequest, 
  orgId, 
  onViewOrgFeed, 
  onStartChat,
  cardColor = "from-[#E96A37] to-orange-500"
}) => {
  const getPlayerCount = () => {
    if (!teamPlayers) return 0;
    if (teamPlayers.type === 'PUBLIC_INFO') return teamPlayers.playerCount;
    if (Array.isArray(teamPlayers)) return teamPlayers.length;
    return 0;
  };

  const getPlayersToShow = () => {
    if (!teamPlayers) return [];
    if (teamPlayers.type === 'PUBLIC_INFO') {
      // For other organizations, show placeholder players
      return Array(Math.min(teamPlayers.playerCount, 4)).fill(null).map((_, index) => ({
        _id: `placeholder-${index}`,
        firstName: 'Player',
        lastName: `${index + 1}`,
        profilePicture: null,
        isPlaceholder: true
      }));
    }
    if (Array.isArray(teamPlayers)) {
      return teamPlayers.slice(0, 4); // Show max 4 players for better layout
    }
    return [];
  };

  const getOrganizationName = () => {
    if (!orgId) return 'Unknown Organization';
    
    // Check different possible name formats
    if (orgId.organizationName) return orgId.organizationName;
    if (orgId.name) return orgId.name;
    if (orgId.firstName && orgId.lastName) return `${orgId.firstName} ${orgId.lastName}`;
    if (orgId.firstName) return orgId.firstName;
    if (orgId.email) return orgId.email;
    
    return 'Unknown Organization';
  };

  const players = getPlayersToShow();
  const playerCount = getPlayerCount();
  const hasMorePlayers = playerCount > 4;

  return (
    <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100">
      {/* Header with gradient background */}
      <div className={`bg-gradient-to-br ${cardColor} px-6 py-8 text-white relative overflow-hidden`}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-12 -translate-x-12"></div>
        </div>
        
        <div className="relative z-10">
          {/* Team Role Badge */}
          <div className="flex justify-between items-start mb-6">
            <div className="bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
              <span className="text-sm font-semibold text-white">
                {isSentRequest ? (isOwnTeam ? 'Your Team' : 'Requesting Team') : (isOwnTeam ? 'Your Team' : 'Opponent Team')}
              </span>
            </div>
            {isOwnTeam && (
              <div className="bg-green-500/20 backdrop-blur-sm rounded-full px-3 py-1">
                <span className="text-xs font-medium text-green-100">Own Team</span>
              </div>
            )}
          </div>

          {/* Team Logo and Basic Info */}
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden ring-2 ring-white/30">
              <ProfileImage
                image={team?.logo}
                user={{ firstName: team?.name, lastName: '' }}
                className="w-full h-full rounded-2xl"
                size="lg"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold text-white mb-1 truncate">
                {team?.name || 'Unknown Team'}
              </h3>
              <p className="text-white/80 text-sm">
                @{team?.uniqueName || 'N/A'}
              </p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{playerCount}</div>
              <div className="text-white/80 text-xs font-medium">Players</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-white capitalize">{team?.type || 'N/A'}</div>
              <div className="text-white/80 text-xs font-medium">Category</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-white capitalize">{team?.gender || 'Mixed'}</div>
              <div className="text-white/80 text-xs font-medium">Gender</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-6">
        {/* Organization Info */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Organization</h4>
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center">
              <ProfileImage
                image={orgId?.profilePicture}
                user={orgId}
                className="w-full h-full rounded-full"
                size="sm"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 truncate">
                {getOrganizationName()}
              </p>
              {orgId?.email && (
                <p className="text-sm text-gray-500 truncate">{orgId.email}</p>
              )}
            </div>
          </div>
        </div>

        {/* Players Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Team Members</h4>
            <span className="text-sm text-gray-400">{playerCount} total</span>
          </div>
          
          {players.length > 0 ? (
            <div className="space-y-3">
              {players.map((player, index) => (
                <div key={player._id || index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <ProfileImage
                    image={player.profilePicture}
                    user={player}
                    className="w-8 h-8 rounded-full flex-shrink-0"
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {player.isPlaceholder ? (
                        <span className="text-gray-600">Player {player.lastName}</span>
                      ) : (
                        `${player.firstName || ''} ${player.lastName || ''}`.trim() || player.userName || 'Unknown Player'
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {player.isPlaceholder ? 'Team Member' : (player.position || player.type || 'Player')}
                    </p>
                  </div>
                  {player.isPlaceholder && (
                    <div className="flex-shrink-0">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
              
              {hasMorePlayers && (
                <div className="text-center py-2">
                  <span className="text-sm text-gray-500 bg-gray-100 rounded-full px-4 py-2">
                    +{playerCount - 4} more members
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-gray-500 text-sm">No team members found</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {!isOwnTeam && (
            <>
              <button
                onClick={() => onViewOrgFeed(orgId)}
                className="w-full flex items-center justify-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-xl transition-all duration-200 border border-gray-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
                <span>View Organization</span>
              </button>
              
              <button
                onClick={() => onStartChat(orgId)}
                className="w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span>Start Conversation</span>
              </button>
            </>
          )}
          
          {isOwnTeam && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <div className="flex items-center justify-center space-x-2 text-green-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">Your Organization's Team</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamCard; 